/**
 * What happened to a resident's visitor passes when they moved out (resident
 * portal Plan 7, RP-7.6).
 *
 * The server moves the passes a leaving resident hosted to whoever stays in the
 * unit, and ends them when nobody does (migration 127, lb_rehost_passes). It
 * answers with `passes: { moved, ended, moved_to }` on the move-out and on
 * "Delete resident". This turns that into the ONE sentence the success toast
 * adds — or nothing at all when the resident hosted no passes, or when the
 * server could not work it out (`null`): a toast never guesses.
 */
export interface MoveOutPasses {
  moved: number;
  ended: number;
  // The first name of the resident the passes moved to; null when unreadable.
  moved_to: string | null;
}

const count = (n: number): string =>
  n === 1 ? '1 visitor pass' : `${n} visitor passes`;

const whole = (n: unknown): number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;

export const moveOutPassesSentence = (
  passes: MoveOutPasses | null | undefined
): string => {
  if (!passes) {
    return '';
  }

  const moved = whole(passes.moved);
  const ended = whole(passes.ended);
  const to = (passes.moved_to || '').trim() || 'another resident of the unit';

  if (moved > 0 && ended > 0) {
    return `${count(moved)} moved to ${to}, ${ended} ended.`;
  }

  if (moved > 0) {
    return `${count(moved)} moved to ${to}.`;
  }

  if (ended > 0) {
    return `${count(ended)} ended.`;
  }

  return '';
};

/** `lead` + the passes sentence: "Ada Lovelace moved out. 2 visitor passes moved to Ana." */
export const withPassesSentence = (
  lead: string,
  passes: MoveOutPasses | null | undefined
): string => {
  const sentence = moveOutPassesSentence(passes);

  return sentence ? `${lead}. ${sentence}` : lead;
};

/** The server's refusal of a future move-out date (RP-D16), said the same way before the round trip. */
export const FUTURE_MOVE_OUT = "The move-out date can't be in the future.";
